import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { AlertCircle, Check, Users } from 'lucide-react';
import Modal from '../modal/Modal';
import { useCenterFeatureFlags } from '../../hooks/useCenterFeatureFlags';
import { usePublicMemberList } from '../../features/publicMember/hooks';
import { useAddMemberCoach } from '../../features/memberCoach/hooks';
import { getMemberAppAccountStatus, ICenterMember } from '../../features/publicMember/types';
import {
    buildShareUsageBarSegments,
    buildShareUsageBreakdown,
    canRetryTemplateShareProvisioning,
    collectSharePoolCloseExpectedItemIds,
    countSharePoolCloseItems,
    getSharePoolId,
    listSharePoolCloseMemberImpacts,
    resolveSharePoolCloseFailure,
    SHARE_CLOSE_EXPECTED_ITEM_ID_LIMIT,
    useSharePoolClosePreview,
    isGroupSharePool,
    MULTI_WRITE_NOTICE,
    resolveSharePoolAdminError,
    SHARED_MEMBERSHIP_SECTION_ID,
    sharedMembershipKeys,
    ShareMemberUsage,
    SharedMembershipEvent,
    SharePoolAdminError,
    SharePoolScope,
    shouldShowParticipantUsage,
    useAddSharedMembershipParticipant,
    useCloseSharedMembershipPool,
    useCreateGroupSharedMembershipPool,
    useCreateSharedMembershipPool,
    useRemoveSharedMembershipParticipant,
    useRetryTemplateShareProvisioning,
    useSharePoolByScope,
    useTemplateShareProvisioning,
    DEFAULT_SHARE_POOL_LIMITS,
} from '../../features/sharedMembership';

type Props = {
    centerId: string;
    /** PT는 membershipId로, 그룹은 memberTicketId + 소유자로 공유 풀을 찾는다. */
    scope: SharePoolScope;
    ownerMemberId: string;
    ownerName: string;
    /** PT 전용. 후보의 담당 강사 연결 여부를 판정하는 기준이다. */
    coachId?: string;
    /** PT 전용. 연결 확인 모달에서 "누구에게 연결할지"를 밝히는 데 쓴다. */
    coachName?: string;
    /** 원본 수강권 상태. 만료된 수강권은 공유로 전환하지 않는다. */
    sourceStatus?: string;
    /** PT 공유 상품으로 발급된 수강권인지. 그룹에는 해당 없음. */
    isTemplateSharedMembership?: boolean;
    /**
     * 공유 상품 발급 건을 **다시 공유해도 되는지**(서버 `shareLifecycle.canReshare`).
     *
     * 서버 판정이 없으면 false 로 온다 — 그때는 버튼을 그리지 않는다. 이 값은 공유 상품
     * 발급 건에만 적용한다. 수동 전환 PT·그룹 수강권의 재공유는 예전 조건 그대로다
     * (그쪽은 `shareLifecycle` 이 없는 그룹 상세에서도 재공유가 되어야 한다).
     */
    canReshare?: boolean;
    isReadOnly: boolean;
    /**
     * 공유 전환 진입점은 하단 액션 줄의 '수강권 전환' 버튼 하나로 합쳤다.
     * 페이지가 이 값을 올릴 때마다 전환 모달을 연다 (같은 값이면 다시 열지 않는다).
     */
    convertRequestKey?: number;
    /** App.tsx의 공통 알럿. 뒤 인자는 두 버튼 알럿(오른쪽=주 버튼, 왼쪽=보조 버튼)용이다. */
    createAlert: (
        title: string,
        content: string,
        isError: boolean,
        buttonContent?: string,
        callBack?: () => void,
        isTwoButton?: boolean,
        twoButtonContent?: string,
        twoButtonCallBack?: () => void,
    ) => void;
};

type ActionModalProps = {
    title: string;
    /** 문자열이면 문단 하나로, 노드면 그대로 그린다(공유 시작처럼 구조화가 필요한 모달용). */
    description: ReactNode;
    confirmText: string;
    isLoading: boolean;
    isDanger?: boolean;
    /** 되돌릴 수 없는 동작에서 확인 절차가 끝나기 전까지 확인 버튼을 잠근다. */
    isConfirmDisabled?: boolean;
    children: ReactNode;
    onClose: () => void;
    onConfirm: () => void;
};

const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 44,
    border: '1px solid #C8D4E8',
    borderRadius: 6,
    padding: '0 12px',
    fontSize: 15,
    color: '#313640',
    backgroundColor: '#FFFFFF',
};

const secondaryButtonClass =
    'h-[40px] px-[18px] rounded-[4px] border border-[#A6BFEF] text-[14px] text-[#2B529D] bg-white hover:bg-[#F4F7FC] disabled:opacity-50 disabled:cursor-not-allowed';
const primaryButtonClass =
    'h-[40px] px-[18px] rounded-[4px] border border-primary text-[14px] text-white bg-primary hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * 수강권 상세의 다른 카드(수업 등록·회차 요약)와 같은 카드 언어를 쓴다.
 * 공유만 파란 테두리로 흰 카드 밖에 떠 있으면 수강권의 속성이 아니라 별개 기능처럼 읽힌다.
 */
const sectionCardClass =
    'w-full mb-[32px] rounded-[16px] border border-solid border-[#E5E7EB] bg-[#FCFDFE] px-[24px] py-[20px]';

/** MEMBERSHIP_USAGE_BUSY 자동 재시도 전 대기 시간. 서버 lease가 풀릴 여유를 준다. */
const USAGE_BUSY_RETRY_DELAY_MS = 400;

const EVENT_ACTION_LABELS: Record<SharedMembershipEvent['action'], string> = {
    POOL_CREATED: '공유 시작',
    POOL_CREATED_FROM_EXISTING: '회차 공유 시작',
    PARTICIPANT_ADDED: '회원 추가',
    PARTICIPANT_REMOVED: '회원 제거',
    MAX_PARTICIPANTS_CHANGED: '동시 인원 한도 변경',
    MAX_LIFETIME_PARTICIPANTS_CHANGED: '누적 인원 한도 변경',
    POOL_CLOSED: '공유 종료',
    OWNER_WITHDRAWAL_REVIEW_REQUIRED: '소유자 탈퇴 — 운영 확인 필요',
    MEMBER_ID_MERGED: '회원 통합 반영',
};

/**
 * 서버가 생명주기 처리에 남기는 reason은 사람이 입력한 문장이 아니라 머신 코드다.
 * 그대로 찍으면 관리자가 왜 빠졌는지 알 수 없다.
 */
const EVENT_REASON_LABELS: Record<string, string> = {
    MEMBER_APP_WITHDRAWAL: '회원 앱 탈퇴',
    COACH_MEMBER_DISCONNECTED: '강사·센터 연결 해제',
};

/** MEMBER_MERGE:{원본}->{대상} 형태로 오므로 접두사만 보고 요약한다(회원 ID 비노출). */
const MEMBER_MERGE_REASON_PREFIX = 'MEMBER_MERGE:';

const formatEventReason = (reason?: string) => {
    const trimmed = (reason || '').trim();
    // 구버전 생성 플로우가 빈 값을 ', ' 같은 구두점 조합으로 저장한 이력이 있다.
    // 사람이 읽을 내용이 없으면 전부 '없음'으로 수렴시킨다.
    if (!trimmed || /^[,.·\s|-]+$/.test(trimmed)) return '입력한 사유 없음';
    if (trimmed.startsWith(MEMBER_MERGE_REASON_PREFIX)) return '회원 통합 반영';
    return EVENT_REASON_LABELS[trimmed] || trimmed;
};

const ActionModal = ({
    title,
    description,
    confirmText,
    isLoading,
    isDanger = false,
    isConfirmDisabled = false,
    children,
    onClose,
    onConfirm,
}: ActionModalProps) => (
    <Modal
        whiteViewWidth={560}
        isCloseButton
        isWhiteOverFlowScroll={false}
        isCloseOutside={!isLoading}
        onClose={isLoading ? undefined : onClose}
    >
        <div role="dialog" aria-label={title} style={{ padding: '36px 40px 32px' }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827' }}>{title}</h2>
            {typeof description === 'string' ? (
                <p
                    style={{
                        marginTop: 12,
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: '#677080',
                        whiteSpace: 'pre-line',
                    }}
                >
                    {description}
                </p>
            ) : (
                <div style={{ marginTop: 12 }}>{description}</div>
            )}
            <div style={{ marginTop: 24 }}>{children}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 28 }}>
                <button
                    type="button"
                    className={secondaryButtonClass}
                    disabled={isLoading}
                    onClick={onClose}
                >
                    취소
                </button>
                <button
                    type="button"
                    className={primaryButtonClass}
                    disabled={isLoading || isConfirmDisabled}
                    onClick={onConfirm}
                    style={
                        isDanger
                            ? {
                                  backgroundColor: '#FF3E4A',
                                  borderColor: '#FF3E4A',
                                  ...(isConfirmDisabled ? { opacity: 0.45 } : {}),
                              }
                            : undefined
                    }
                >
                    {isLoading ? '처리 중...' : confirmText}
                </button>
            </div>
        </div>
    </Modal>
);

/** 공유 시작이 무엇을 바꾸고 무엇을 그대로 두는지. 문단 대신 항목으로 세운다. */
const SHARE_START_EFFECTS: Array<{ tone: 'keep' | 'change'; label: string; detail: string }> = [
    { tone: 'keep', label: '원본 수강권 · 결제 기록', detail: '그대로 유지됩니다' },
    { tone: 'change', label: '잔여 회차', detail: '참여 회원과 함께 차감합니다' },
    { tone: 'keep', label: '되돌리기', detail: '공유 종료로 언제든 되돌립니다' },
];

/**
 * 전환 전 서버가 다시 확인하는 것들. "미지정 일정, 초과 예약, 진행 중인 양도를
 * 서버에서 다시 확인합니다"를 한 문장으로 흘려보내면 무엇이 막힐 수 있는지 안 읽힌다.
 */
const SHARE_START_PRECHECKS = ['미지정 일정', '초과 예약', '진행 중인 양도'];

const ShareStartBrief = ({
    isGroupSource,
    isRestart = false,
}: {
    isGroupSource: boolean;
    isRestart?: boolean;
}) => (
    <div className="flex flex-col gap-[12px]">
        {isRestart && (
            <p className="m-0 rounded-[10px] border-l-[3px] border-solid border-[#A6BFEF] bg-[#F4F7FC] px-[12px] py-[10px] text-[12px] leading-[19px] text-[#2B529D]">
                이전 참여 회원은 자동으로 다시 추가되지 않습니다. 필요한 회원을 다시 선택해주세요.
                누적 참여 이력은 그대로 이어집니다.
            </p>
        )}
        <ul className="m-0 flex list-none flex-col gap-[2px] rounded-[10px] border border-solid border-[#EEF1F5] bg-[#FAFBFD] p-[14px]">
            {SHARE_START_EFFECTS.map((effect) => (
                <li key={effect.label} className="flex items-start gap-[8px] py-[4px]">
                    <span
                        aria-hidden
                        className="mt-[2px] shrink-0"
                        style={{ color: effect.tone === 'keep' ? '#0A7563' : '#B4632D', lineHeight: 0 }}
                    >
                        {effect.tone === 'keep' ? <Check size={15} strokeWidth={2.6} /> : <Users size={15} />}
                    </span>
                    <span className="text-[13px] leading-[19px] text-[#505967]">
                        <b className="font-semibold text-[#313640]">{effect.label}</b> — {effect.detail}
                    </span>
                </li>
            ))}
        </ul>

        <p className="m-0 flex items-start gap-[8px] rounded-[10px] border-l-[3px] border-solid border-[#F0C36D] bg-[#FFF8E6] px-[12px] py-[10px] text-[12px] leading-[19px] text-[#8A5A00]">
            <span aria-hidden className="mt-[1px] shrink-0" style={{ lineHeight: 0 }}>
                <AlertCircle size={15} />
            </span>
            {/* 문구를 여기에 복사해 두면 상수를 고쳐도 이 모달만 옛말이 남는다. 상수를 그대로 쓴다. */}
            <span>{MULTI_WRITE_NOTICE}</span>
        </p>

        {!isGroupSource && (
            <p className="m-0 text-[12px] leading-[18px] text-[#98A2B3]">
                시작 전에 {SHARE_START_PRECHECKS.join(' · ')}를 서버에서 다시 확인합니다. 하나라도
                남아 있으면 시작이 막히고 이유를 알려드립니다.
            </p>
        )}
    </div>
);

const REASON_MAX_LENGTH = 500;

/**
 * 되돌리기 어려운 동작(참여자 제거·공유 종료)은 사유를 반드시 남긴다.
 * 서버가 사유를 선택 입력으로 받으므로 기본 문구를 대신 채워 넣지 않는다 —
 * 아무도 입력하지 않은 문장이 감사 이력에 진짜 사유처럼 남는 편이 더 나쁘다.
 */

const ReasonField = ({
    label = '변경 사유',
    value,
    onChange,
    isRequired = false,
}: {
    /** 모달 맥락에 맞춘 라벨 — 전환 모달은 "전환 사유", 종료 모달은 "종료 사유". */
    label?: string;
    value: string;
    onChange: (value: string) => void;
    isRequired?: boolean;
}) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 18 }}>
        <span
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 13,
                color: '#677080',
            }}
        >
            <span>
                {label}
                <span style={{ marginLeft: 4, color: isRequired ? '#B4232D' : '#98A2B3' }}>
                    {isRequired ? '(필수)' : '(선택)'}
                </span>
            </span>
            <span style={{ fontSize: 12, color: '#98A2B3' }}>
                {value.length}/{REASON_MAX_LENGTH}
            </span>
        </span>
        <textarea
            aria-label={label}
            value={value}
            maxLength={REASON_MAX_LENGTH}
            onChange={(event) => onChange(event.target.value)}
            placeholder="변경 이력에 남길 내용이 있으면 입력하세요."
            style={{
                ...inputStyle,
                minHeight: 92,
                padding: 12,
                resize: 'vertical',
                lineHeight: 1.5,
            }}
        />
    </label>
);

/** 후속 동작이 필요한 오류는 안내 문장을 덧붙여 다음 행동을 분명히 한다. */
const buildAdminErrorMessage = (resolved: SharePoolAdminError, targetMemberName = '') => {
    switch (resolved.action) {
        case 'RESOLVE_FUTURE_USAGE': {
            // 어느 회원의 예약을 정리해야 하는지, 어디서 확인하는지까지 안내한다 (가이드 §4).
            const target = targetMemberName ? `${targetMemberName} 회원` : '해당 회원';
            return `${resolved.message}\n\n${target}의 미래 예약과 승인 대기 신청을 정리한 뒤 다시 시도해주세요. 예약은 이 화면 아래 수강권 사용 기록(수업 내역)에서 확인할 수 있습니다.`;
        }
        case 'OPEN_EXISTING_POOL':
            return `${resolved.message}\n\n이미 있는 공유 정보를 다시 불러왔으니 화면에서 확인해주세요.`;
        case 'RAISE_PARTICIPANT_LIMIT':
            // 한도 변경 UI는 제거됐다(기본 상한이 사실상 무제한이라 도달할 일이 없다).
            // 그래도 서버가 한도 오류를 주면 운영 문의로 안내한다.
            return `${resolved.message}\n\n운영 담당자에게 문의해주세요.`;
        case 'CONTACT_SUPPORT':
            // 서버 문구가 이미 운영 문의를 안내하면 같은 말을 두 번 쓰지 않는다.
            return resolved.message.includes('운영 담당자')
                ? resolved.message
                : `${resolved.message}\n\n같은 요청을 반복하지 말고 운영 담당자에게 문의해주세요.`;
        case 'HIDE_FEATURE':
            return `${resolved.message}\n\n화면을 새로고침하면 관련 메뉴가 사라집니다.`;
        default:
            return resolved.message;
    }
};

/** 한도 변경 이력은 전/후 값이 있어야 의미가 있다. 값이 온전할 때만 "N명 → M명"을 붙인다. */
const formatEventLimitChange = (event: SharedMembershipEvent): string => {
    const [before, after] =
        event.action === 'MAX_PARTICIPANTS_CHANGED'
            ? [event.beforeMaxParticipants, event.afterMaxParticipants]
            : event.action === 'MAX_LIFETIME_PARTICIPANTS_CHANGED'
            ? [event.beforeMaxLifetimeParticipants, event.afterMaxLifetimeParticipants]
            : [undefined, undefined];
    return typeof before === 'number' && typeof after === 'number'
        ? ` · ${before}명 → ${after}명`
        : '';
};

const useDebouncedValue = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const timeout = window.setTimeout(() => setDebouncedValue(value), delay);
        return () => window.clearTimeout(timeout);
    }, [delay, value]);
    return debouncedValue;
};

/** 시각 축(막대)과 목록의 짝을 맞추는 색 점. 범례 없이도 어느 줄인지 읽히게 한다. */
const ColorDot = ({ color }: { color: string }) => (
    <span
        aria-hidden
        className="inline-block h-[10px] w-[10px] shrink-0 rounded-full"
        style={{ backgroundColor: color }}
    />
);

const SharedMembershipSection = ({
    centerId,
    scope,
    ownerMemberId,
    ownerName,
    coachId,
    coachName,
    sourceStatus,
    isTemplateSharedMembership = false,
    canReshare = false,
    isReadOnly,
    convertRequestKey = 0,
    createAlert,
}: Props) => {
    const queryClient = useQueryClient();
    const {
        isSharedMembershipReadEnabled,
        isSharedMembershipCreatePoolEnabled,
        isSharedMembershipMutateParticipantsEnabled,
    } = useCenterFeatureFlags();
    const isGroupSource = scope.sourceType === 'MEMBER_TICKET';
    /** 연결 확인 문구에 쓸 강사 표시 이름. 이름을 모르면 "담당 강사"로 부른다. */
    const coachDisplayName = coachName?.trim() ? `${coachName.trim()} 강사` : '담당 강사';
    const membershipId = scope.sourceType === 'MEMBERSHIP' ? scope.membershipId : '';

    const poolQuery = useSharePoolByScope({
        scope,
        enabled: isSharedMembershipReadEnabled,
    });
    // 공유 상품 발급 복구는 PT 템플릿 발급에만 있다.
    const provisioningQuery = useTemplateShareProvisioning({
        membershipId,
        enabled: isSharedMembershipReadEnabled && !isGroupSource && isTemplateSharedMembership,
    });
    const pool = poolQuery.data;
    const sharePoolId = getSharePoolId(pool);
    const isGroupPool = isGroupSource || isGroupSharePool(pool);
    const isTemplateIssuedMembership =
        isTemplateSharedMembership || pool?.origin === 'TEMPLATE';
    /**
     * 공유를 끝내는 동작(참여자 제거·공유 종료)은 읽기 전용 회원 화면에서도 열어둔다.
     * 소유자가 앱을 탈퇴해도 서버는 풀을 ACTIVE로 유지하므로, 여기서 막으면 참여자들이
     * 계속 탈퇴자 수강권으로 예약하는데 관리자에게는 끝낼 수단이 없어진다.
     */
    const canWindDownPool =
        isSharedMembershipReadEnabled &&
        isSharedMembershipMutateParticipantsEnabled &&
        pool?.status === 'ACTIVE' &&
        !!sharePoolId;
    /** 참여자 추가처럼 공유를 넓히는 동작은 읽기 전용 회원에게 계속 막는다. */
    const canMutate = canWindDownPool && !isReadOnly;
    const isSourceActive = String(sourceStatus || '').toUpperCase() === 'ACTIVE';
    /**
     * 종료 이력은 감사 목적으로 그대로 두고 새 ACTIVE 풀을 만든다.
     *
     * 공유 상품 발급 PT도 이제 열린다 — 단 **서버 판정(`canReshare`)이 있을 때만**이다.
     * 그 판정에는 발급 완료·공유 종료뿐 아니라 수강권이 활성·미삭제·미취소인지까지
     * 들어 있다. 판정을 못 받았으면(구버전 서버·판정 실패) false 라 예전처럼 닫힌다.
     *
     * 수동 전환 PT와 그룹 수강권에는 이 조건을 걸지 않는다. 그룹 상세 엔드포인트는
     * `shareLifecycle` 을 싣지 않아, 걸면 되던 재공유가 막히는 회귀가 된다.
     */
    const isResharePermitted =
        pool?.status === 'CLOSED' &&
        isSourceActive &&
        (isTemplateIssuedMembership ? canReshare : true);
    const canRestartClosedPool =
        isSharedMembershipCreatePoolEnabled && isResharePermitted && !isReadOnly;

    const createPoolMutation = useCreateSharedMembershipPool();
    const createGroupPoolMutation = useCreateGroupSharedMembershipPool();
    const addParticipantMutation = useAddSharedMembershipParticipant(scope);
    const removeParticipantMutation = useRemoveSharedMembershipParticipant(scope);
    const closePoolMutation = useCloseSharedMembershipPool(scope);
    const retryProvisioningMutation = useRetryTemplateShareProvisioning(membershipId);
    const addMemberCoachMutation = useAddMemberCoach();
    const isCreating = createPoolMutation.isLoading || createGroupPoolMutation.isLoading;

    const [action, setAction] = useState<'convert' | 'close' | null>(null);
    /**
     * 종료 프리뷰 — **종료 모달을 열 때만** 부른다.
     *
     * 이 목록이 곧 `expectedItemIds` 가 되므로 캐시하지 않는다(hooks 의 staleTime 0).
     * 실패(구버전 서버 404 포함)하면 정리 흐름을 열지 않고 오늘과 같은 차단이 남는다.
     */
    const closePreviewQuery = useSharePoolClosePreview({
        sharePoolId: sharePoolId ?? '',
        enabled: action === 'close' && !!sharePoolId,
    });
    /**
     * **재조회가 실패하면 이전 목록을 쓰지 않는다.**
     *
     * React Query 는 refetch 오류에도 기존 `data` 를 보존하고 `fetchStatus` 만 idle 로
     * 되돌린다. 그대로 읽으면 SHARE_CLOSE_PLAN_CHANGED 뒤 재조회가 실패했을 때
     * **낡은 목록이 다시 살아나고 확인 서명까지 그대로 유효**해진다. 여기서 null 로
     * 끊으면 서명이 달라져 확인이 풀리고 정리 UI 도 닫힌다 — 잠금을 따로 걸 필요가 없다.
     */
    const closeImpact = closePreviewQuery.isError ? null : closePreviewQuery.data ?? null;
    const closeExpectedItemIds = useMemo(
        () => collectSharePoolCloseExpectedItemIds(closeImpact),
        [closeImpact],
    );
    /**
     * 확인은 **이 목록에 대한 것**이다. 목록이 바뀌면 자동으로 풀린다.
     *
     * 불리언 하나로 두면 프리뷰가 갱신돼도(SHARE_CLOSE_PLAN_CHANGED 뒤 refetch 등)
     * 확인이 그대로 남아, 관리자가 **보지 못한 수업까지 취소**하게 된다. 취소는
     * 되돌릴 수 없다. 그래서 확인한 목록의 서명을 들고 있다가 지금 목록과 대조한다 —
     * 목록이 바뀌는 어느 경로를 놓쳐도 안전하다.
     */
    const closeItemSignature = closeExpectedItemIds.join(',');
    const [acknowledgedItemSignature, setAcknowledgedItemSignature] = useState<string | null>(
        null,
    );
    const isCleanupAcknowledged =
        acknowledgedItemSignature !== null && acknowledgedItemSignature === closeItemSignature;
    /** 서버 Joi 상한(1000)을 넘으면 400 이라 시도조차 하지 않는다. */
    const isCloseCleanupTooLarge = closeExpectedItemIds.length > SHARE_CLOSE_EXPECTED_ITEM_ID_LIMIT;
    /**
     * 정리를 확인해서 보낼 수 있는 상태인가.
     *
     * 그룹(MEMBER_TICKET) 풀은 `supportsCleanup: false` 로 온다 — 자동 정리를 지원하지
     * 않으므로 확인 UI 를 열지 않는다. 그 경우 종료는 기존 방식대로 시도되고, 미래 일정이
     * 있으면 서버가 오늘과 똑같이 막는다.
     */
    const supportsCloseCleanup = closeImpact?.supportsCleanup === true;
    /**
     * 취소 건수는 **실제로 보낼 id 개수**다. 서버 `cancelableCount` 를 쓰면 "3건이
     * 취소됩니다"라고 해놓고 4건을 보내는 자리가 생긴다.
     */
    const closeCleanupCount = closeExpectedItemIds.length;
    const needsCleanupAcknowledgement = supportsCloseCleanup && closeCleanupCount > 0;
    const canConfirmCleanup =
        supportsCloseCleanup &&
        !isCloseCleanupTooLarge &&
        (closeCleanupCount === 0 || isCleanupAcknowledged);
    /*
     * 프리뷰를 못 받았을 때(구버전 서버·조회 실패) 확인 버튼을 잠글지 검토했으나
     * **열어 둔다.** 그 상태에서는 예약이 있는지조차 알 수 없어, 잠그면 예약이 없어
     * 정상적으로 종료되는 경우까지 막는다. 예약이 있으면 서버가 오늘과 똑같이 거절하고
     * 그 사유가 그대로 안내된다.
     */
    const [removeTarget, setRemoveTarget] = useState<ShareMemberUsage | null>(null);
    const [reason, setReason] = useState('');
    const [search, setSearch] = useState('');
    // 참여자 추가는 가끔 쓰는 동작이다. 검색창을 항상 펼쳐두면 현황(막대·목록)이 아래로 밀린다.
    const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
    // 사유를 입력받아 놓고 접어두면 아무도 못 본다. 기본으로 펼쳐 둔다.
    const [isHistoryVisible, setIsHistoryVisible] = useState(true);
    // 공유 규칙은 한 번 배우면 되는 제약이다. 상주 배너 대신 필요할 때만 편다.
    const [isRuleVisible, setIsRuleVisible] = useState(false);
    const [knownParticipantNames, setKnownParticipantNames] = useState<Record<string, string>>({});
    /**
     * 담당 강사에 연결되지 않은 회원을 고른 상태.
     * 연결은 정산·회원목록에도 반영되는 동작이라 조용히 처리하지 않고 한 번 확인받는다.
     */
    const [coachLinkTarget, setCoachLinkTarget] = useState<ICenterMember | null>(null);
    // 전환 모달에서 미리 고른 참여 회원. 풀이 생긴 뒤 순차로 추가한다.
    const [selectedInvitees, setSelectedInvitees] = useState<ICenterMember[]>([]);
    /**
     * 그중 담당 강사에 아직 연결되지 않은 회원.
     * 연결 여부는 고른 시점에만 알 수 있다(검색어가 바뀌면 판정 근거가 사라진다).
     */
    const [unlinkedInviteeIds, setUnlinkedInviteeIds] = useState<Set<string>>(new Set());
    const debouncedSearch = useDebouncedValue(search.trim(), 300);

    const isConvertModalOpen = action === 'convert';
    // 그룹 공유는 센터 스코프라 담당 강사 개념이 없다.
    const candidateCoachId = isGroupPool ? undefined : pool?.coachId || coachId;
    const isCandidateSearchEnabled =
        (canMutate || isConvertModalOpen) && debouncedSearch.length >= 2;

    /**
     * 후보 검색은 담당 강사로 좁히지 않는다.
     *
     * 예전에는 coach 필터를 걸어 미연결 회원이 아예 안 떴는데, 그러면 관리자는
     * "이 회원이 왜 검색되지 않는지"조차 알 수 없다 — 없는 회원인지, 이름을 틀렸는지,
     * 연결이 안 된 건지 구분이 안 된다. 전부 보여주고 연결 여부를 표시한 뒤,
     * 미연결이면 그 자리에서 연결까지 이어준다.
     */
    const candidateQuery = usePublicMemberList({
        centerId,
        status: 'active',
        search: debouncedSearch,
        page: 1,
        limit: 20,
        enabled: isCandidateSearchEnabled,
    });

    /**
     * 같은 검색을 담당 강사로 좁힌 결과. 두 목록의 차집합이 "미연결"이다.
     * 서버의 coach 필터와 공유 추가 가드가 같은 Rapport 관계를 보므로 판정 기준이 일치한다.
     * 조회에 실패하면 배지만 생략한다 — 추가 자체는 서버 응답으로 처리하므로 막지 않는다.
     */
    const connectedCandidateQuery = usePublicMemberList({
        centerId,
        status: 'active',
        search: debouncedSearch,
        coach: candidateCoachId,
        page: 1,
        limit: 20,
        enabled: isCandidateSearchEnabled && !!candidateCoachId,
    });
    const connectedMemberIds = useMemo(
        () =>
            new Set(
                (connectedCandidateQuery.data?.results || []).map((member) =>
                    String(member.memberId || ''),
                ),
            ),
        [connectedCandidateQuery.data?.results],
    );
    /** 연결 여부를 아직/영영 확인할 수 없으면 배지를 달지 않는다(잘못된 경고가 더 나쁘다). */
    const canTellCoachConnection =
        !!candidateCoachId && connectedCandidateQuery.isSuccess && !connectedCandidateQuery.isError;
    const isCandidateConnected = (member: ICenterMember) =>
        !canTellCoachConnection || connectedMemberIds.has(String(member.memberId || ''));

    const participantIds = useMemo(
        // CLOSED 풀의 참여자는 과거 이력이다. 새 풀은 빈 참여자 목록으로 시작하므로
        // 같은 회원을 다시 초대할 수 있어야 한다. ACTIVE 풀에서만 중복 후보를 제외한다.
        () =>
            new Set(
                (pool?.status === 'ACTIVE' ? pool.participantMemberIds || [] : []).map(String),
            ),
        [pool?.participantMemberIds, pool?.status],
    );
    const selectedInviteeIds = useMemo(
        () => new Set(selectedInvitees.map((member) => String(member.memberId))),
        [selectedInvitees],
    );
    const candidates = useMemo(() => {
        const results = candidateQuery.data?.results || [];
        return results.filter((member) => {
            const candidateId = String(member.memberId || '');
            return (
                !!candidateId &&
                candidateId !== String(ownerMemberId) &&
                !participantIds.has(candidateId) &&
                !selectedInviteeIds.has(candidateId)
            );
        });
    }, [candidateQuery.data?.results, ownerMemberId, participantIds, selectedInviteeIds]);

    const closeAction = () => {
        setAction(null);
        setRemoveTarget(null);
        setCoachLinkTarget(null);
        setReason('');
        setSearch('');
        setSelectedInvitees([]);
        setUnlinkedInviteeIds(new Set());
        setAcknowledgedItemSignature(null);
    };

    /**
     * 페이지의 '수강권 전환' 모달에서 공유를 고르면 여기로 신호가 온다.
     * 초기값 0은 무시한다 — 화면을 열자마자 전환 모달이 뜨면 안 된다.
     */
    useEffect(() => {
        if (!convertRequestKey) return;
        setReason('');
        setSearch('');
        setSelectedInvitees([]);
        setAction('convert');
    }, [convertRequestKey]);

    /**
     * 회원 검색 결과 목록. 전환 모달(선택)과 사용 중 섹션(즉시 추가)이 함께 쓴다.
     * 로딩·조회 실패·결과 없음 처리를 한 곳에 둬서 두 화면이 갈라지지 않게 한다.
     */
    const renderCandidateResults = ({
        actionLabel,
        connectActionLabel,
        onPick,
        isPickBusy,
        pendingMemberId,
    }: {
        actionLabel: string;
        /**
         * 미연결 회원 줄에 쓸 라벨. 고르는 즉시 연결까지 이어지는 화면에서만 넘긴다.
         * 전환 모달처럼 나중에 처리하는 곳에서 이 말을 쓰면 지키지 못할 약속이 된다.
         */
        connectActionLabel?: string;
        onPick: (member: ICenterMember) => void;
        isPickBusy: boolean;
        /** 지금 추가 중인 회원. 어느 줄이 처리 중인지 보이게 한다. */
        pendingMemberId?: string;
    }) => (
        // 결과가 최대 20건이라 높이를 제한한다. 모달 안에서 쓰면 화면 밖으로 밀린다.
        <div className="mt-[8px] max-h-[240px] overflow-y-auto rounded-[8px] border border-[#DCE3ED] bg-white shadow-[0_6px_16px_rgba(16,24,40,0.08)]">
            {candidateQuery.isLoading ? (
                <p className="px-[16px] py-[12px] text-[13px] text-[#677080]">검색 중...</p>
            ) : candidateQuery.isError ? (
                // 조회 실패를 '검색 결과 없음'으로 보여주면 관리자가
                // 존재하는 회원을 계속 다시 입력하게 된다.
                <div className="px-[16px] py-[12px] flex items-center justify-between gap-[12px]">
                    <p className="text-[13px] text-[#8C3239]">
                        회원 검색에 실패했습니다. 네트워크 상태를 확인해주세요.
                    </p>
                    <button
                        type="button"
                        className={secondaryButtonClass}
                        onClick={() => void candidateQuery.refetch()}
                    >
                        다시 조회
                    </button>
                </div>
            ) : candidates.length === 0 ? (
                <p className="px-[16px] py-[12px] text-[13px] text-[#677080]">
                    {(candidateQuery.data?.results || []).length > 0
                        ? '검색된 회원이 모두 이미 선택되었거나 소유자입니다.'
                        : '검색 결과가 없습니다. 이름을 다시 확인해주세요.'}
                </p>
            ) : (
                candidates.map((member) => {
                    const appStatus = getMemberAppAccountStatus(member);
                    const disabled = appStatus === 'withdrawn' || member.isReadOnly === true;
                    const isPending = pendingMemberId === member.memberId;
                    // 미연결이어도 줄을 막지 않는다. 고르면 연결까지 이어주는 게 이 화면의 목적이다.
                    const needsCoachLink = !disabled && !isCandidateConnected(member);
                    return (
                        // 줄 전체가 버튼이다. 검색 → 클릭 한 번으로 끝나게 한다.
                        <button
                            key={member.memberId}
                            type="button"
                            disabled={disabled || isPickBusy}
                            onClick={() => onPick(member)}
                            className="w-full flex items-center justify-between gap-[12px] px-[16px] py-[12px] text-left border-b last:border-b-0 border-[#EEF1F5] hover:bg-[#F4F7FC] disabled:cursor-not-allowed disabled:hover:bg-white"
                        >
                            <span className="min-w-0">
                                <span className="text-[14px] font-semibold text-[#313640]">{member.name}</span>
                                <span className="ml-[8px] text-[12px] text-[#98A2B3]">
                                    {appStatus === 'linked' ? '앱 연동' : appStatus === 'withdrawn' ? '앱 탈퇴' : '앱 미연동'}
                                </span>
                                {disabled && (
                                    <span className="ml-[8px] text-[12px] text-[#B4232D]">추가할 수 없음</span>
                                )}
                                {needsCoachLink && (
                                    <span className="ml-[8px] rounded-[4px] bg-[#FFF4D6] px-[6px] py-[2px] text-[11px] font-semibold text-[#8A5A00]">
                                        강사 연결 필요
                                    </span>
                                )}
                            </span>
                            <span
                                className={`shrink-0 text-[13px] font-semibold ${
                                    disabled ? 'text-[#C4CBD6]' : needsCoachLink ? 'text-[#8A5A00]' : 'text-[#2B529D]'
                                }`}
                            >
                                {isPending
                                    ? '추가 중...'
                                    : needsCoachLink && connectActionLabel
                                    ? connectActionLabel
                                    : actionLabel}
                            </span>
                        </button>
                    );
                })
            )}
        </div>
    );

    /** RESOLVE_FUTURE_USAGE 안내에 쓸 회원 표시 이름. 못 찾으면 빈 문자열. */
    const resolveKnownMemberName = (memberId?: string) => {
        if (!memberId) return '';
        const normalizedId = String(memberId);
        if (normalizedId === String(ownerMemberId)) return ownerName;
        if (pool?.ownerSummary && String(pool.ownerSummary.memberId) === normalizedId) {
            return pool.ownerSummary.name;
        }
        const summary = (pool?.participantSummaries || []).find(
            (participant) => String(participant.memberId) === normalizedId,
        );
        return summary?.name || knownParticipantNames[normalizedId] || '';
    };

    // 버튼 연타·모달 중복 확인이 같은 요청을 두 번 보내지 않도록 잠근다.
    // mutation.isLoading만으로는 자동 재시도(RETRY_ONCE) 사이의 틈을 막지 못한다.
    const submissionLockRef = useRef(false);

    /**
     * 공유 풀 변경 요청을 실행하고 실패는 가이드 §4 규칙대로 처리한다.
     * - MEMBERSHIP_USAGE_BUSY(details.retryable): 잠시 기다렸다 같은 입력으로 자동 1회 재시도
     * - 동시 변경 / 이미 존재: 풀을 다시 조회해 화면을 최신 상태로 맞춘 뒤 안내
     */
    const runPoolAction = async (
        run: () => Promise<unknown>,
        failureTitle: string,
        /**
         * 특정 실패를 기본 알럿 대신 직접 처리하고 싶을 때 쓴다.
         * true를 돌려주면 기본 알럿을 띄우지 않는다.
         */
        /**
         * 두 번째 인자는 **원본 에러**다. `resolved` 에는 details 가 없어서, 서버가 실어
         * 보낸 숫자(취소된 건수 등)를 읽으려면 원본이 필요하다. 기존 호출부는 이 인자를
         * 쓰지 않으므로 동작이 바뀌지 않는다.
         */
        handleResolvedError?: (resolved: SharePoolAdminError, rawError: unknown) => boolean,
    ) => {
        if (submissionLockRef.current) return false;
        submissionLockRef.current = true;
        try {
            await run();
            return true;
        } catch (error) {
            let lastError: unknown = error;
            let resolved = resolveSharePoolAdminError(error as { status?: number; body?: unknown });

            if (resolved.action === 'RETRY_ONCE') {
                // 서버의 사용량 lease가 풀릴 시간을 준 뒤 재시도해야 재충돌 확률이 낮다.
                await new Promise((resolve) => window.setTimeout(resolve, USAGE_BUSY_RETRY_DELAY_MS));
                try {
                    await run();
                    return true;
                } catch (retryError) {
                    lastError = retryError;
                    resolved = resolveSharePoolAdminError(
                        retryError as { status?: number; body?: unknown },
                    );
                }
            }

            if (
                resolved.action === 'REFETCH_POOL' ||
                resolved.action === 'OPEN_EXISTING_POOL' ||
                resolved.action === 'HIDE_FEATURE'
            ) {
                await poolQuery.refetch();
            }

            if (resolved.action === 'HIDE_FEATURE') {
                // 센터 플래그 캐시를 무효화해 새로고침 없이도 섹션이 실제로 사라지게 한다.
                queryClient.invalidateQueries({ queryKey: ['centerFeatureFlags'] });
            }

            if (!handleResolvedError?.(resolved, lastError)) {
                createAlert(
                    failureTitle,
                    buildAdminErrorMessage(resolved, resolveKnownMemberName(resolved.memberId)),
                    true,
                );
            }
            return false;
        } finally {
            submissionLockRef.current = false;
        }
    };

    /**
     * 종료 확인 목록의 행. **소유자와 참여자를 나눠서 보여주기 위한 것이다.**
     *
     * 이름은 서버 값을 먼저 쓰고, 비어 있으면 화면이 이미 아는 이름으로 되찾는다
     * (센터 별칭을 못 찾은 경우가 있다). 그래도 없으면 이름을 지어내지 않고 역할만 쓴다.
     */
    const closeCleanupMemberRows = useMemo(
        () =>
            listSharePoolCloseMemberImpacts(closeImpact)
                // **`items` 기준으로 세고 거른다.** 서버 카운트로 그리면 화면에 없는
                // 항목이 취소되거나 건수가 어긋난다 — 전송되는 것은 items 뿐이다.
                .map((usage) => ({ usage, counts: countSharePoolCloseItems(usage.items) }))
                .filter(({ counts }) => counts.totalCount > 0)
                .map(({ usage, counts }) => {
                    const roleLabel = usage.role === 'OWNER' ? '소유자' : '참여자';
                    const name = usage.name || resolveKnownMemberName(usage.memberId);
                    const parts: string[] = [];
                    if (counts.scheduleCount > 0) parts.push(`수업 ${counts.scheduleCount}건`);
                    if (counts.requestCount > 0) parts.push(`대기 신청 ${counts.requestCount}건`);
                    return {
                        memberId: usage.memberId,
                        label: name || `이름 미확인 (${roleLabel})`,
                        roleLabel: name ? roleLabel : '',
                        countLabel: parts.join(' · ') || `${counts.totalCount}건`,
                    };
                }),
        // resolveKnownMemberName 은 pool 스냅샷에서 이름을 찾는다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [closeImpact, pool, knownParticipantNames, ownerMemberId, ownerName],
    );

    /**
     * 종료 모달 설명. **상태마다 할 말이 다르다.**
     *
     * 특히 정리를 지원하지 않는 경우(그룹 풀·구버전 서버)에는 "공유를 먼저 종료하세요"
     * 같은 되돌릴 수 없는 행동을 권하지 않는다. 지금 할 수 있는 것만 말한다.
     */
    const closeModalDescription = (() => {
        if (closePreviewQuery.isLoading) return '';
        if (closePreviewQuery.isError || !closeImpact) {
            // 구버전 서버에는 프리뷰가 없다. 오늘과 같은 조건이 그대로 적용된다.
            return '참여자들의 미래 일정과 승인 대기 신청이 모두 정리된 경우에만 종료됩니다. 원본 수강권과 사용 이력은 삭제되지 않습니다.';
        }
        if (!supportsCloseCleanup) {
            return '그룹 수강권 공유는 예약 자동 정리를 아직 지원하지 않아요. 예정된 수업이 남아 있으면 종료되지 않습니다. 원본 수강권과 사용 이력은 삭제되지 않습니다.';
        }
        if (closeCleanupCount === 0) {
            return '취소될 예정된 수업이 없어요. 원본 수강권과 사용 이력은 삭제되지 않습니다.';
        }
        return '종료하면 아래 예정된 수업과 대기 신청이 함께 취소됩니다. 원본 수강권과 사용 이력은 삭제되지 않습니다.';
    })();

    const submittedReason = () => reason.trim();

    /** 시작 시 함께 연결해야 하는 초대 대상. 안내 문구와 생성 플로우가 함께 쓴다. */
    const unlinkedInviteeInvitees = selectedInvitees.filter((member) =>
        unlinkedInviteeIds.has(String(member.memberId)),
    );

    /** 전환 직후 참여자 추가에 실패한 회원을 한 줄로 요약한다. */
    const summarizeInviteFailure = (resolved: SharePoolAdminError) =>
        resolved.errorCode === 'SHARE_MEMBER_NOT_CONNECTED'
            ? '담당 강사 배정 후 다시 추가해주세요.'
            : resolved.message;

    /**
     * 전환 결과를 알럿 한 번으로 알린다. 회원마다 띄우면 확인만 여러 번 하게 된다.
     * 부분 실패는 풀을 되돌리지 않는다 — 성공한 참여자와 감사 이력이 이미 남았고,
     * 실패한 회원만 섹션의 추가 UI에서 다시 시도하면 된다.
     */
    const announceConversionResult = (
        invitees: ICenterMember[],
        failures: Array<{ member: ICenterMember; resolved: SharePoolAdminError }>,
    ) => {
        if (invitees.length === 0) {
            createAlert('회차 공유 시작', '이제 함께 쓸 회원을 추가할 수 있습니다.', false);
            return;
        }

        const addedCount = invitees.length - failures.length;
        if (failures.length === 0) {
            createAlert(
                '회차 공유 시작',
                `함께 쓸 회원 ${addedCount}명을 추가했습니다.`,
                false,
            );
            return;
        }

        const failureLines = failures
            .map(({ member, resolved }) => `· ${member.name}님 — ${summarizeInviteFailure(resolved)}`)
            .join('\n');

        /**
         * 강사 미연결로 실패한 회원은 아래 [+ 회원 추가]에서 연결까지 한 번에 처리할 수 있다.
         * 첫 번째 회원 이름을 검색창에 미리 채우고 패널을 열어, 알럿을 닫자마자 바로 이어가게 한다.
         * 새 탭으로 회원 프로필을 열게 하던 예전 경로는 작업 맥락을 잃고 수동 작업이 남았다.
         */
        const unlinked = isGroupPool
            ? []
            : failures.filter(
                  ({ resolved }) => resolved.errorCode === 'SHARE_MEMBER_NOT_CONNECTED',
              );
        const nextAction = unlinked.length > 0
            ? `아래 ${unlinked.length === 1 ? '회원은' : '회원들은'} 담당 강사에 연결되어 있지 않습니다. [+ 회원 추가]에서 고르면 연결과 추가를 한 번에 처리합니다.`
            : '아래 회원은 추가하지 못했습니다. 공유 수강권 카드의 [+ 회원 추가]에서 다시 시도할 수 있습니다.';

        const content = [
            addedCount > 0
                ? `공유를 시작하고 ${addedCount}명을 추가했습니다.`
                : '공유를 시작했습니다.',
            '',
            nextAction,
            failureLines,
        ].join('\n');

        if (unlinked.length > 0) {
            setIsAddPanelOpen(true);
            setSearch(unlinked[0].member.name);
        }
        createAlert('공유 시작 — 일부 회원 추가 실패', content, true);
    };

    const handleCreatePool = async () => {
        // 인원 한도는 화면에서 받지 않고 서버 전역 상한과 같은 고정값을 보낸다 (사실상 무제한).
        const limits = {
            ...DEFAULT_SHARE_POOL_LIMITS,
            reason: submittedReason(),
        };
        const invitees = selectedInvitees;
        const failures: Array<{ member: ICenterMember; resolved: SharePoolAdminError }> = [];

        const succeeded = await runPoolAction(async () => {
            const createdPool =
                scope.sourceType === 'MEMBER_TICKET'
                    ? await createGroupPoolMutation.mutateAsync({
                          centerId,
                          sourceMemberTicketId: scope.memberTicketId,
                          ...limits,
                      })
                    : await createPoolMutation.mutateAsync({
                          centerId,
                          sourceMembershipId: scope.membershipId,
                          ...limits,
                      });

            const createdSharePoolId = getSharePoolId(createdPool);
            if (!createdSharePoolId) return;

            // 순차 호출한다. 병렬로 보내면 인원 한도 경합과 오류 순서가 꼬인다.
            for (const member of invitees) {
                try {
                    // 모달에서 "시작할 때 함께 연결됩니다"라고 밝힌 회원만 연결한다.
                    // 연결이 실패하면 추가도 어차피 막히므로 같은 catch로 묶어 한 줄로 보고한다.
                    if (candidateCoachId && unlinkedInviteeIds.has(String(member.memberId))) {
                        await addMemberCoachMutation.mutateAsync({
                            memberId: member.memberId,
                            coachId: candidateCoachId,
                            centerId,
                        });
                    }
                    await addParticipantMutation.mutateAsync({
                        sharePoolId: createdSharePoolId,
                        memberId: member.memberId,
                    });
                } catch (error) {
                    failures.push({
                        member,
                        resolved: resolveSharePoolAdminError(
                            error as { status?: number; body?: unknown },
                        ),
                    });
                }
            }
        }, '회차 공유 시작 실패');

        if (!succeeded) return;
        // 추가에 성공한 회원만 기록한다. 응답 요약에 이름이 빠져도 목록에서 이름으로 보이게 하는 fallback이다.
        const failedIds = new Set(failures.map(({ member }) => String(member.memberId)));
        setKnownParticipantNames((names) => {
            const nextNames = { ...names };
            for (const member of invitees) {
                const memberId = String(member.memberId);
                if (!failedIds.has(memberId)) nextNames[memberId] = member.name;
            }
            return nextNames;
        });
        queryClient.invalidateQueries({ queryKey: sharedMembershipKeys.all });
        closeAction();
        announceConversionResult(invitees, failures);
    };

    /** 추가 성공 후 뒷정리. 연결 후 재시도 경로와 공유한다. */
    const finishAddParticipant = (member: ICenterMember, didLinkCoach: boolean) => {
        setKnownParticipantNames((names) => ({ ...names, [member.memberId]: member.name }));
        setSearch('');
        setCoachLinkTarget(null);
        // 한 명 추가하면 대개 끝난다. 패널을 접어 현황(막대·목록)을 바로 확인하게 한다.
        setIsAddPanelOpen(false);
        createAlert(
            didLinkCoach ? '강사 연결 후 추가 완료' : '회원 추가 완료',
            didLinkCoach
                ? `${member.name} 회원을 ${coachDisplayName}에 연결하고 공유에 추가했습니다.`
                : `${member.name} 회원이 이 회차를 함께 쓰게 됩니다.`,
            false,
        );
    };

    const handleAddParticipant = async (member: ICenterMember, didLinkCoach = false) => {
        if (!sharePoolId || !member.memberId) return;

        // 목록에서 이미 미연결로 판정된 회원이면 실패가 확정된 요청을 보내지 않는다.
        // 바로 연결 확인으로 넘어간다. (판정이 어긋난 경우는 아래 409 처리로 받는다.)
        if (!didLinkCoach && !isGroupPool && candidateCoachId && !isCandidateConnected(member)) {
            setCoachLinkTarget(member);
            return;
        }

        const succeeded = await runPoolAction(
            () =>
                addParticipantMutation.mutateAsync({
                    sharePoolId,
                    memberId: member.memberId,
                }),
            '회원 추가 실패',
            (resolved) => {
                // PT 풀의 참여자는 같은 담당 강사와 연결된 회원만 가능하다.
                // 목록의 '미연결' 배지는 검색 결과 차집합으로 추정한 값이라 어긋날 수 있으므로,
                // 서버가 거절했을 때도 같은 연결 플로우로 이어지게 한다.
                // 그룹 풀은 센터 소속만 보므로 서버가 이 코드를 반환하지 않는다.
                if (resolved.errorCode !== 'SHARE_MEMBER_NOT_CONNECTED' || isGroupPool) return false;
                // 이미 연결까지 하고도 거절당했다면 같은 화면을 다시 띄워봐야 소용없다.
                if (didLinkCoach) return false;
                setCoachLinkTarget(member);
                return true;
            },
        );
        if (!succeeded) return;
        finishAddParticipant(member, didLinkCoach);
    };

    /**
     * 담당 강사에 연결하고 곧바로 공유에 추가한다.
     * 연결만 되고 추가가 실패하면 관리자는 "뭐가 된 건지" 알 수 없으므로 결과를 나눠 알린다.
     */
    const handleLinkCoachAndAdd = async () => {
        const member = coachLinkTarget;
        if (!member || !candidateCoachId) return;

        try {
            await addMemberCoachMutation.mutateAsync({
                memberId: member.memberId,
                coachId: candidateCoachId,
                centerId,
            });
        } catch (error) {
            const resolved = resolveSharePoolAdminError(
                error as { status?: number; body?: unknown },
                '강사 연결에 실패했습니다.',
            );
            createAlert('강사 연결 실패', buildAdminErrorMessage(resolved), true);
            return;
        }

        // 연결 결과가 후보 목록(연결 여부 배지)에 바로 반영되게 한다.
        await connectedCandidateQuery.refetch();
        await handleAddParticipant(member, true);
    };

    const handleRemoveParticipant = async () => {
        if (!sharePoolId || !removeTarget) return;
        // 사유는 선택 입력이다. 필수로 두면 운영자가 아무 문구나 채워 넣어 감사 이력이
        // 그 문구로 오염된다. 서버도 빈 값을 허용한다(membershipShare.validation.js).
        const removedName = removeTarget.name;
        const succeeded = await runPoolAction(
            () =>
                removeParticipantMutation.mutateAsync({
                    sharePoolId,
                    memberId: removeTarget.memberId,
                    reason: submittedReason(),
                }),
            '회원 제거 실패',
        );
        if (!succeeded) return;
        closeAction();
        createAlert('회원 제거 완료', `${removedName} 회원을 공유에서 제거했습니다.`, false);
    };

    const handleClosePool = async () => {
        if (!sharePoolId) return;
        /**
         * 정리를 확인했을 때만 `confirmCleanup` 을 보낸다.
         *
         * 프리뷰를 못 받았거나(구버전 서버·조회 실패) 그룹 풀이면 보내지 않는다. 그러면
         * 서버가 오늘과 똑같이 SHARE_MEMBER_HAS_FUTURE_USAGE 로 막는다 — 더 엄격한 쪽이라
         * 안전하고, 기존 동작 그대로다.
         *
         * `expectedItemIds` 는 화면이 그린 목록·확인 서명과 **같은 배열**이다.
         * 여기서 다시 계산하지 않는다 — 같은 식을 두 번 쓰면 축이 둘이 되고,
         * 되돌릴 수 없는 취소에서 그 둘이 갈리는 날이 온다.
         */
        const cleanupArgs = canConfirmCleanup
            ? { confirmCleanup: true, expectedItemIds: closeExpectedItemIds }
            : {};
        /**
         * 성공 안내는 **서버가 실제로 취소한 수**로 말한다.
         *
         * 보낸 id 개수로 말하면 안 된다. 서버는 현재 항목이 확인 집합의 부분집합인지만
         * 검사하므로, 그 사이 다른 곳에서 일부가 이미 취소됐다면 보낸 개수보다 적게
         * 취소된다. 그 차이를 관리자가 "내가 N건을 취소했다"로 기억하면 회원 안내가
         * 어긋난다.
         */
        let canceledCountToReport: number | null = null;

        // 사유는 선택 입력이다. 위 제거와 같은 이유다.
        const succeeded = await runPoolAction(
            async () => {
                const result = await closePoolMutation.mutateAsync({
                    sharePoolId,
                    reason: submittedReason(),
                    ...cleanupArgs,
                });
                // 정리를 요청했는데 서버가 결과를 안 주면 **0으로 단정하지 않는다.**
                // 응답 본문이 비거나 깨져도 0건으로 보이면, 실제로 취소가 일어났는데
                // "취소된 수업 없음"으로 안내하게 된다.
                canceledCountToReport = cleanupArgs.confirmCleanup
                    ? result.cleanup?.canceledCount ?? null
                    : 0;
            },
            '공유 종료 실패',
            (_resolved, rawError) => {
                /**
                 * 종료 정리 전용 실패 셋은 기본 알럿보다 먼저 잡는다. 기본 문구는
                 * "다시 시도해주세요" 톤이라, 재시도하면 안 되는 경우까지 재시도를 권한다.
                 */
                const failure = resolveSharePoolCloseFailure(
                    (rawError as { body?: unknown; data?: unknown })?.body ??
                        (rawError as { data?: unknown })?.data,
                );
                if (!failure) return false;
                // 확인 목록이 낡았으면 새 목록을 받아야 한다. 낡은 목록으로 다시 보내면
                // 같은 거절이 반복된다. (확인 서명이 달라져 체크도 자동으로 풀린다.)
                if (failure.needsRefreshedPreview) closePreviewQuery.refetch();
                /**
                 * **서버가 재시도해도 안 된다고 하면 모달을 닫는다.**
                 *
                 * 여기서 할 수 있는 일이 없는데 모달과 확인이 남아 있으면 관리자가 다시
                 * 누른다. 그때마다 남은 항목이 또 취소되고 회원에게 알림이 나간다 —
                 * 이미 일부가 취소된 뒤라 더 위험하다. 상태를 다시 보게 돌려보낸다.
                 */
                if (!failure.retryable && !failure.needsRefreshedPreview) closeAction();
                createAlert(failure.title, failure.message, true);
                return true;
            },
        );
        if (!succeeded) return;
        closeAction();
        const tail = '잔여 회차와 결제 기록은 소유자 수강권에 그대로 남습니다.';
        createAlert(
            '공유 종료 완료',
            canceledCountToReport === null
                ? `공유를 종료했습니다. 취소된 수업 수는 확인하지 못했어요 — 예약 현황을 한 번 확인해주세요.\n${tail}`
                : canceledCountToReport > 0
                  ? `공유를 종료하고 예정된 수업 ${canceledCountToReport}건을 취소했습니다. 해당 회원에게는 알림이 나갔어요.\n${tail}`
                  : `공유를 종료했습니다. ${tail}`,
            false,
        );
    };

    const handleRetryProvisioning = async () => {
        const provisioning = provisioningQuery.data;
        if (!canRetryTemplateShareProvisioning(provisioning)) return;

        try {
            const nextProvisioning = await retryProvisioningMutation.mutateAsync();
            await Promise.all([provisioningQuery.refetch(), poolQuery.refetch()]);
            if (nextProvisioning.status === 'COMPLETED') {
                createAlert('공유 설정 완료', '수강권의 공유 설정이 완료되었습니다.', false);
            } else if (nextProvisioning.status === 'REVIEW_REQUIRED') {
                createAlert(
                    '운영 확인 필요',
                    '자동 복구를 계속할 수 없습니다. 확인 코드를 포함해 고객센터에 문의해주세요.',
                    true,
                );
            } else {
                createAlert(
                    '공유 설정 확인 필요',
                    '수강권은 정상 발급되어 있습니다. 공유 설정 상태만 다시 확인해주세요.',
                    true,
                );
            }
        } catch (error) {
            await provisioningQuery.refetch();
            const resolved = resolveSharePoolAdminError(
                error as { status?: number; body?: unknown },
                '공유 설정을 다시 시도하지 못했습니다.',
            );
            createAlert('공유 설정 재시도 실패', buildAdminErrorMessage(resolved), true);
        }
    };

    if (!isSharedMembershipReadEnabled) return null;

    if (
        poolQuery.isLoading ||
        (!isGroupSource && isTemplateSharedMembership && !pool && provisioningQuery.isLoading)
    ) {
        return (
            <section className={sectionCardClass}>
                <p className="text-[14px] text-[#677080]">공유 수강권 정보를 확인하고 있습니다.</p>
            </section>
        );
    }

    if (poolQuery.isError) {
        return (
            <section className={`${sectionCardClass} flex items-center justify-between`}>
                <p className="text-[14px] text-[#8C3239]">공유 수강권 정보를 불러오지 못했습니다.</p>
                <button type="button" className={secondaryButtonClass} onClick={() => poolQuery.refetch()}>
                    다시 시도
                </button>
            </section>
        );
    }

    if (!pool || (canRestartClosedPool && action === 'convert')) {
        if (!pool && !isGroupSource && isTemplateSharedMembership) {
            if (provisioningQuery.isError || !provisioningQuery.data) {
                return (
                    <section className={`${sectionCardClass} flex items-center justify-between gap-[24px]`}>
                        <div>
                            <h2 className="text-[18px] font-bold text-[#313640]">공유 설정 상태 확인 필요</h2>
                            <p className="mt-[8px] text-[14px] leading-[22px] text-[#8C3239]">
                                이 수강권은 공유 상품으로 발급되었습니다. 상태 조회에 실패했으므로 새 수강권을 발급하거나 기존 수강권을 공유로 전환하지 마세요.
                            </p>
                        </div>
                        <button
                            type="button"
                            className={secondaryButtonClass}
                            onClick={() => provisioningQuery.refetch()}
                        >
                            상태 다시 확인
                        </button>
                    </section>
                );
            }

            const provisioning = provisioningQuery.data;

            /**
             * 발급은 끝났는데 풀이 조회되지 않는 경우.
             * 조회는 includeClosed로 종료 풀까지 받으므로 정상 종료는 여기까지 오지 않고
             * 아래 본 화면에서 '종료됨'으로 그려진다. 여기 남는 건 (a) 풀이 실제로 없거나
             * (b) 센터 스코프 밖이라 서버가 존재를 마스킹한 경우다. 둘을 구분할 수 없으니
             * 장애로 단정하지 않고, 소유자 프로필에서 확인하도록 안내만 한다.
             */
            if (provisioning.status === 'COMPLETED') {
                return (
                    <section className={sectionCardClass}>
                        <div className="flex items-center gap-[8px]">
                            <h2 className="text-[18px] font-bold text-[#313640]">공유 수강권</h2>
                            <span className="rounded-full bg-[#F2F4F8] px-[10px] py-[4px] text-[12px] font-semibold text-[#5B6472]">
                                확인 필요
                            </span>
                        </div>
                        <p className="mt-[8px] text-[14px] leading-[22px] text-[#677080]">
                            이 화면에서는 공유 정보를 확인할 수 없습니다. 소유자 회원 프로필에서 확인해주세요.
                            잔여 회차는 소유자 수강권에 그대로 남아 있습니다.
                        </p>
                    </section>
                );
            }

            const canRetryProvisioning = canRetryTemplateShareProvisioning(provisioning);
            const statusLabel =
                provisioning.status === 'PENDING'
                    ? '처리 중'
                    : provisioning.status === 'FAILED'
                    ? '설정 실패'
                    : '운영 확인 필요';
            const statusDescription =
                provisioning.status === 'PENDING'
                    ? '수강권 발급은 완료되었고 공유 설정을 처리 중입니다. 수강권 전체를 다시 발급하지 마세요.'
                    : provisioning.status === 'FAILED'
                    ? '수강권 발급은 완료되었지만 공유 설정만 실패했습니다. 아래 버튼으로 공유 설정만 안전하게 재시도할 수 있습니다.'
                    : '자동 복구를 계속할 수 없는 상태입니다. 확인 코드를 포함해 고객센터 또는 운영 담당자에게 문의해주세요.';

            return (
                <section className={`${sectionCardClass} flex items-center justify-between gap-[24px]`}>
                    <div>
                        <div className="flex items-center gap-[8px]">
                            <h2 className="text-[18px] font-bold text-[#313640]">공유 수강권</h2>
                            <span className="rounded-full bg-[#FFF4D6] px-[10px] py-[4px] text-[12px] font-semibold text-[#8A5A00]">
                                {statusLabel}
                            </span>
                        </div>
                        <p className="mt-[8px] text-[14px] leading-[22px] text-[#677080]">
                            {statusDescription}
                        </p>
                        {provisioning.errorCode && (
                            <p className="mt-[8px] text-[12px] text-[#8C3239]">
                                확인 코드: {provisioning.errorCode}
                            </p>
                        )}
                    </div>
                    <div className="flex gap-[8px] shrink-0">
                        <button
                            type="button"
                            className={secondaryButtonClass}
                            disabled={retryProvisioningMutation.isLoading}
                            onClick={() => {
                                provisioningQuery.refetch();
                                poolQuery.refetch();
                            }}
                        >
                            상태 새로고침
                        </button>
                        {canRetryProvisioning && (
                            <button
                                type="button"
                                className={primaryButtonClass}
                                disabled={retryProvisioningMutation.isLoading}
                                onClick={handleRetryProvisioning}
                            >
                                {retryProvisioningMutation.isLoading ? '재시도 중...' : '공유 설정 재시도'}
                            </button>
                        )}
                    </div>
                </section>
            );
        }

        if (!isSharedMembershipCreatePoolEnabled || isReadOnly || !isSourceActive) return null;
        const isRestarting = pool?.status === 'CLOSED';
        /**
         * 공유를 시작하기 전에는 화면에 아무것도 그리지 않는다.
         * 진입점은 하단 액션 줄의 '수강권 전환' 버튼 하나로 합쳤다 —
         * 예전처럼 여기에 큰 안내 카드를 두면 '그룹 전환'(버튼)과 '공유로 전환'(카드)이
         * 다른 자리에 떨어져 있어 둘의 차이를 눈으로 비교할 수 없다.
         */
        return (
            <>
                {action === 'convert' && (
                    <ActionModal
                        title={
                            isRestarting
                                ? isGroupSource
                                    ? '그룹 수강권 회차 공유 다시 시작'
                                    : '회차 공유 다시 시작'
                                : isGroupSource
                                ? '그룹 수강권 회차 공유 시작'
                                : '회차 공유 시작'
                        }
                        description={
                            <ShareStartBrief
                                isGroupSource={isGroupSource}
                                isRestart={isRestarting}
                            />
                        }
                        confirmText={isRestarting ? '다시 공유 시작' : '공유 시작'}
                        isLoading={isCreating}
                        onClose={closeAction}
                        onConfirm={handleCreatePool}
                    >
                        <div>
                            <div className="flex items-baseline justify-between gap-[8px]">
                                <span className="text-[13px] font-semibold text-[#374151]">
                                    함께 쓸 회원
                                    <span className="ml-[4px] text-[12px] font-normal text-[#98A2B3]">(선택)</span>
                                </span>
                                {/* 몇 명을 골랐는지가 이 단계의 진행 상태다. 칩을 세지 않아도 보이게 한다. */}
                                <span className="text-[12px] text-[#98A2B3]">
                                    {selectedInvitees.length > 0
                                        ? `${selectedInvitees.length}명 선택`
                                        : '지금 고르지 않아도 됩니다'}
                                </span>
                            </div>
                            <p className="mt-[4px] text-[12px] leading-[18px] text-[#98A2B3]">
                                {isGroupSource
                                    ? '같은 센터에 연결된 회원만 검색됩니다. 공유 시작 후에도 언제든 추가할 수 있습니다.'
                                    : '같은 센터와 담당 강사에 연결된 회원만 검색됩니다. 공유 시작 후에도 언제든 추가할 수 있습니다.'}
                            </p>
                            {selectedInvitees.length > 0 && (
                                <div className="mt-[12px] flex flex-wrap gap-[8px]">
                                    {selectedInvitees.map((member) => (
                                        <span
                                            key={member.memberId}
                                            className="inline-flex items-center gap-[6px] rounded-full bg-[#EDF4FF] px-[10px] py-[5px] text-[13px] text-[#2B529D]"
                                        >
                                            {member.name}
                                            {unlinkedInviteeIds.has(String(member.memberId)) && (
                                                <span className="rounded-[4px] bg-[#FFF4D6] px-[5px] py-[1px] text-[11px] font-semibold text-[#8A5A00]">
                                                    연결 필요
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                aria-label={`${member.name} 선택 해제`}
                                                className="text-[#7B8493] hover:text-[#313640]"
                                                onClick={() => {
                                                    setSelectedInvitees((members) =>
                                                        members.filter(
                                                            (selected) => selected.memberId !== member.memberId,
                                                        ),
                                                    );
                                                    setUnlinkedInviteeIds((ids) => {
                                                        const next = new Set(ids);
                                                        next.delete(String(member.memberId));
                                                        return next;
                                                    });
                                                }}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                            <input
                                aria-label="참여 회원 검색"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="회원 이름을 2글자 이상 입력해주세요."
                                style={{ ...inputStyle, marginTop: 12 }}
                            />
                            {debouncedSearch.length >= 2 &&
                                renderCandidateResults({
                                    actionLabel: '선택',
                                    onPick: (member) => {
                                        setSelectedInvitees((members) => [...members, member]);
                                        if (!isGroupSource && !isCandidateConnected(member)) {
                                            setUnlinkedInviteeIds((ids) => {
                                                const next = new Set(ids);
                                                next.add(String(member.memberId));
                                                return next;
                                            });
                                        }
                                        setSearch('');
                                    },
                                    isPickBusy: false,
                                })}
                            {/* 고른 뒤 실패로 알리지 않는다. 시작 버튼을 누르기 전에 무엇이 일어날지 밝힌다. */}
                            {unlinkedInviteeInvitees.length > 0 && (
                                <p className="mt-[10px] rounded-[8px] border-l-[3px] border-solid border-[#F0C36D] bg-[#FFF8E6] px-[12px] py-[8px] text-[12px] leading-[19px] text-[#8A5A00]">
                                    {unlinkedInviteeInvitees.map((member) => member.name).join(', ')} 회원은
                                    아직 {coachDisplayName}에게 연결되어 있지 않습니다. 공유를 시작할 때 함께
                                    연결됩니다.
                                </p>
                            )}
                        </div>

                        <ReasonField
                            label={isRestarting ? '재공유 사유' : '공유 시작 사유'}
                            value={reason}
                            onChange={setReason}
                        />
                    </ActionModal>
                )}
            </>
        );
    }

    const breakdown = buildShareUsageBreakdown(pool, ownerName, knownParticipantNames);
    const barSegments = buildShareUsageBarSegments(breakdown);
    const members = breakdown.members;
    const unresolvedParticipantCount = members.filter((member) => !member.isNameResolved).length;
    const ownerDisplayName = members[0]?.name || ownerName || '수강권 소유자';
    const showParticipantUsage = shouldShowParticipantUsage(pool);
    const remainingCount = breakdown.bookableRemaining;
    const takenCount =
        breakdown.capacity !== null && remainingCount !== null
            ? Math.max(0, breakdown.capacity - remainingCount)
            : null;
    const isActivePool = pool.status === 'ACTIVE';
    const events = pool.events || [];

    /**
     * 잔여가 바닥나면 참여자 모두가 예약을 못 잡는다. 개인 수강권보다 파장이 커서
     * 숫자 색만으로 알리지 않고 임계값에서 문구를 함께 띄운다.
     */
    const isRemainingCritical = remainingCount !== null && remainingCount <= 0;
    const isRemainingLow = remainingCount !== null && remainingCount > 0 && remainingCount <= 3;
    const remainingColor = isRemainingCritical || isRemainingLow ? '#B4232D' : '#111827';

    return (
        <>
            <section id={SHARED_MEMBERSHIP_SECTION_ID} className={sectionCardClass}>
                <div className="flex items-start justify-between gap-[24px]">
                    <div className="min-w-0">
                        <div className="flex items-center gap-[8px]">
                            <h2 className="text-[18px] font-bold text-[#111827]">공유 수강권</h2>
                            {/* 종료 응답이 캐시에 먼저 반영되므로 배지는 실제 status에서 파생한다. */}
                            {isActivePool ? (
                                <span className="rounded-full bg-[#E8F8F5] px-[10px] py-[4px] text-[12px] font-semibold text-[#008977]">
                                    공유 중
                                </span>
                            ) : (
                                <span className="rounded-full bg-[#F2F4F8] px-[10px] py-[4px] text-[12px] font-semibold text-[#5B6472]">
                                    공유 종료
                                </span>
                            )}
                            <span className="rounded-full bg-[#F2F4F8] px-[10px] py-[4px] text-[12px] font-semibold text-[#5B6472]">
                                {isGroupPool ? '그룹' : 'PT'}
                            </span>
                        </div>
                        <p className="mt-[6px] text-[14px] leading-[21px] text-[#6B7280]">
                            {/*
                              "공유 상품으로 발급된 수강권은 다시 공유할 수 없다"는 문구를
                              여기서 걷어냈다 — 서버가 종료 뒤 재공유를 열면서 사실이 아니게 됐다.
                              대신 실제 재공유 가능 여부(isResharePermitted)로 가른다. 발급 미완·
                              기록 불일치·만료·취소 건은 여전히 못 하므로 약속하지 않는다.
                            */}
                            {isActivePool
                                ? `${ownerDisplayName}님의 회차를 참여 회원이 함께 사용합니다. 예약한 순서대로 차감됩니다.`
                                : isResharePermitted
                                ? '공유가 종료되었습니다. 잔여 회차는 소유자 수강권에 그대로 남아 있으며, 새 공유로 다시 시작할 수 있습니다.'
                                : '공유가 종료되었습니다. 잔여 회차는 소유자 수강권에 그대로 남아 있으며, 지금은 새 공유를 시작할 수 없습니다.'}
                        </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-[8px]">
                        {/* 상주 배너 대신 필요할 때 펴는 링크로 둔다. 항상 떠 있으면 아무도 읽지 않는다. */}
                        <button
                            type="button"
                            aria-expanded={isRuleVisible}
                            className="h-[32px] rounded-[6px] border border-[#E5E7EB] bg-white px-[12px] text-[13px] text-[#5B6472] hover:bg-[#F4F7FC]"
                            onClick={() => setIsRuleVisible((visible) => !visible)}
                        >
                            공유 규칙 {isRuleVisible ? '접기' : '보기'}
                        </button>
                        {canWindDownPool && (
                            <button
                                type="button"
                                className="h-[32px] rounded-[6px] border border-[#F1AEB3] bg-white px-[12px] text-[13px] font-semibold text-[#B4232D] hover:bg-[#FFF1F2]"
                                onClick={() => {
                                    setReason('');
                                    setAction('close');
                                }}
                            >
                                공유 종료
                            </button>
                        )}
                        {canRestartClosedPool && (
                            <button
                                type="button"
                                className={primaryButtonClass}
                                onClick={() => {
                                    setReason('');
                                    setSearch('');
                                    setSelectedInvitees([]);
                                    setUnlinkedInviteeIds(new Set());
                                    setAction('convert');
                                }}
                            >
                                다시 공유 시작
                            </button>
                        )}
                    </div>
                </div>

                {isRuleVisible && (
                    <p className="mt-[12px] rounded-[8px] border-l-[3px] border-[#F0C36D] bg-[#FFF8E6] px-[12px] py-[8px] text-[12px] leading-[19px] text-[#8A5A00]">
                        {MULTI_WRITE_NOTICE}
                    </p>
                )}

                {isReadOnly && canWindDownPool && (
                    <p className="mt-[12px] rounded-[8px] border-l-[3px] border-[#F1AEB3] bg-[#FFF1F2] px-[12px] py-[8px] text-[13px] leading-[20px] text-[#8C3239]">
                        소유자가 탈퇴했거나 보존된 회원인데 공유는 아직 사용 중입니다. 참여자가 계속 이
                        수강권으로 예약할 수 있으니 참여자를 정리하고 공유를 종료해주세요.
                    </p>
                )}

                {/*
                  회차 분배 — 이 화면의 핵심.
                  "총 몇 회를 누가 얼마나 가져갔고 몇 회 남았나"를 막대 하나로 답한다.
                  참여자 행 끝의 작은 숫자만으로는 3명만 넘어가도 비교가 되지 않는다.
                */}
                <div className="mt-[20px] rounded-[14px] border border-solid border-[#EEF1F5] bg-white px-[20px] py-[16px]">
                    <div className="flex flex-wrap items-end justify-between gap-[12px]">
                        <div>
                            <p className="text-[13px] font-semibold text-[#374151]">회차 분배</p>
                            <p className="mt-[2px] text-[12px] text-[#98A2B3]">
                                {breakdown.capacity === null
                                    ? '총 회차를 확인할 수 없습니다'
                                    : `총 ${breakdown.capacity}회 중 ${takenCount ?? 0}회 사용·예약`}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-[12px] text-[#6B7280]">예약 가능</p>
                            <p
                                className="text-[24px] font-bold leading-[30px]"
                                style={{ color: remainingColor }}
                            >
                                {remainingCount === null ? '-' : `${remainingCount}회`}
                            </p>
                        </div>
                    </div>

                    {barSegments.length > 0 && (
                        // 막대는 이 화면에서 유일하게 '분배'를 말하는 요소다.
                        // 그래픽으로만 두면 화면을 못 보는 사용자에게는 아무 정보도 남지 않는다.
                        <div
                            role="img"
                            aria-label={`회차 분배 — 총 ${breakdown.capacity}회 중 ${barSegments
                                .map((segment) => `${segment.label} ${segment.count}회`)
                                .join(', ')}`}
                            className="mt-[12px] flex h-[14px] w-full overflow-hidden rounded-full bg-[#EEF1F5]"
                        >
                            {barSegments.map((segment) => (
                                <div
                                    key={segment.key}
                                    title={`${segment.label} ${segment.count}회`}
                                    style={{
                                        width: `${segment.percent}%`,
                                        backgroundColor: segment.color,
                                        // 1~2회짜리 세그먼트가 폭 0으로 사라지지 않게 최소 폭을 준다.
                                        minWidth: segment.count > 0 ? 4 : 0,
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    {barSegments.length > 0 && (
                        <div className="mt-[10px] flex flex-wrap items-center gap-x-[16px] gap-y-[6px]">
                            {barSegments.map((segment) => (
                                <span
                                    key={segment.key}
                                    className="flex items-center gap-[6px] text-[12px] text-[#5B6472]"
                                >
                                    <ColorDot color={segment.isRemaining ? '#C4CBD6' : segment.color} />
                                    {segment.label}
                                    <b className="font-semibold text-[#313640]">{segment.count}회</b>
                                </span>
                            ))}
                        </div>
                    )}

                    {isActivePool && (isRemainingCritical || isRemainingLow) && (
                        <p className="mt-[12px] rounded-[8px] bg-[#FFF1F2] px-[12px] py-[8px] text-[12px] leading-[19px] text-[#8C3239]">
                            {isRemainingCritical
                                ? '예약 가능한 회차가 없습니다. 참여자 누구도 새 예약을 잡을 수 없으니 회차를 늘리거나 공유를 정리해주세요.'
                                : `예약 가능한 회차가 ${remainingCount}회 남았습니다. 참여자 ${members.length}명이 함께 쓰므로 곧 소진됩니다.`}
                        </p>
                    )}

                    {!breakdown.hasMemberBreakdown && (
                        <p className="mt-[12px] text-[12px] leading-[18px] text-[#98A2B3]">
                            그룹 공유는 회차를 사람별로 나눠 집계하지 않아 참여자별 사용량을 표시하지 않습니다.
                        </p>
                    )}
                </div>

                <div className="mt-[20px]">
                    <div className="flex items-center justify-between gap-[12px]">
                        <h3 className="text-[13px] font-semibold text-[#374151]">
                            함께 쓰는 회원 {members.length}명
                        </h3>
                        {canMutate && (
                            <button
                                type="button"
                                aria-expanded={isAddPanelOpen}
                                className="h-[32px] rounded-[6px] border border-[#A6BFEF] bg-white px-[12px] text-[13px] font-semibold text-[#2B529D] hover:bg-[#F4F7FC]"
                                onClick={() => {
                                    setSearch('');
                                    setIsAddPanelOpen((open) => !open);
                                }}
                            >
                                {isAddPanelOpen ? '추가 닫기' : '+ 회원 추가'}
                            </button>
                        )}
                    </div>

                    {canMutate && isAddPanelOpen && (
                        <div className="mt-[12px] rounded-[12px] border border-solid border-[#DCE3ED] bg-white px-[16px] py-[14px]">
                            {/* 검색창 아래에 결과가 바로 붙고, 줄을 누르면 그대로 추가된다. */}
                            <div className="w-[420px] max-w-full">
                                <input
                                    autoFocus
                                    aria-label="공유 참여 회원 검색"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder="회원 이름을 2글자 이상 입력하세요"
                                    style={inputStyle}
                                />
                                {search.trim().length > 0 && search.trim().length < 2 ? (
                                    <p className="mt-[8px] text-[12px] text-[#98A2B3]">
                                        2글자 이상 입력하면 회원을 찾습니다.
                                    </p>
                                ) : (
                                    debouncedSearch.length >= 2 &&
                                    renderCandidateResults({
                                        actionLabel: '추가',
                                        connectActionLabel: '연결 후 추가',
                                        onPick: handleAddParticipant,
                                        isPickBusy: addParticipantMutation.isLoading,
                                        pendingMemberId: addParticipantMutation.isLoading
                                            ? addParticipantMutation.variables?.memberId
                                            : undefined,
                                    })
                                )}
                            </div>
                            <p className="mt-[8px] text-[12px] text-[#98A2B3]">
                                {isGroupPool
                                    ? '같은 센터에 연결된 회원만 검색됩니다. 전화번호 등 개인정보는 표시하지 않습니다.'
                                    : '같은 센터와 담당 강사에 연결된 회원만 검색됩니다. 전화번호 등 개인정보는 표시하지 않습니다.'}
                            </p>
                        </div>
                    )}

                    <ul
                        aria-label="함께 쓰는 회원"
                        className="mt-[12px] overflow-hidden rounded-[12px] border border-solid border-[#E3E8F0] bg-white"
                    >
                        {members.map((member) => (
                            <li
                                key={member.memberId}
                                className="flex items-center gap-[12px] border-b border-[#EEF1F5] px-[16px] py-[12px] last:border-b-0"
                            >
                                {/* 막대 세그먼트와 같은 색. 어느 줄이 어느 구간인지 범례 없이 읽힌다. */}
                                <ColorDot color={member.color} />
                                <span className="min-w-0 truncate text-[14px] font-semibold text-[#313640]">
                                    {member.name}
                                </span>
                                {member.isOwner && (
                                    <span className="shrink-0 rounded-full bg-[#EDF4FF] px-[8px] py-[2px] text-[11px] font-semibold text-[#2B529D]">
                                        소유자
                                    </span>
                                )}
                                {member.isDeleted && (
                                    <span className="shrink-0 text-[12px] text-[#B4232D]">삭제된 회원</span>
                                )}

                                {showParticipantUsage ? (
                                    // 숫자는 오른쪽 고정폭 정렬. 자릿수가 흔들리면 줄끼리 비교가 안 된다.
                                    <span className="ml-auto flex shrink-0 items-center gap-[14px] text-[12px] tabular-nums text-[#6B7280]">
                                        <span className="w-[58px] text-right">
                                            사용 <b className="font-semibold text-[#313640]">{member.usedCount}</b>
                                        </span>
                                        <span className="w-[58px] text-right">
                                            예약 <b className="font-semibold text-[#313640]">{member.reservedCount}</b>
                                        </span>
                                        <span className="w-[52px] text-right text-[#98A2B3]">
                                            합 {member.takenCount}회
                                        </span>
                                    </span>
                                ) : (
                                    <span className="ml-auto" />
                                )}

                                {canWindDownPool && !member.isOwner && (
                                    <button
                                        type="button"
                                        disabled={!member.isNameResolved}
                                        title={
                                            member.isNameResolved
                                                ? `${member.name} 회원을 공유에서 제거`
                                                : '이름을 확인할 수 없어 제거할 수 없습니다'
                                        }
                                        className="ml-[8px] h-[28px] shrink-0 rounded-[6px] border border-[#E3E8F0] bg-white px-[10px] text-[12px] text-[#6B7280] hover:border-[#F1AEB3] hover:bg-[#FFF1F2] hover:text-[#B4232D] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[#E3E8F0] disabled:hover:bg-white disabled:hover:text-[#6B7280]"
                                        onClick={() => {
                                            setReason('');
                                            setRemoveTarget(member);
                                        }}
                                    >
                                        제거
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>

                    {members.length === 1 && (
                        <p className="mt-[12px] rounded-[8px] bg-[#F7F9FC] px-[16px] py-[12px] text-[13px] leading-[20px] text-[#677080]">
                            아직 함께 쓰는 회원이 없습니다. 회원을 추가하기 전까지는 소유자만 이 회차를 사용합니다.
                        </p>
                    )}
                    {unresolvedParticipantCount > 0 && (
                        <p className="mt-[12px] rounded-[8px] bg-[#FFF8E6] px-[16px] py-[12px] text-[13px] leading-[20px] text-[#8A5A00]">
                            이름을 확인할 수 없는 참여자 {unresolvedParticipantCount}명은 안전을 위해 이 화면에서 제거할 수 없습니다. 새로고침 후에도 같다면 고객센터로 문의해주세요.
                        </p>
                    )}
                    {!canMutate && (
                        <p className="mt-[12px] rounded-[8px] bg-[#F7F9FC] px-[16px] py-[12px] text-[13px] text-[#677080]">
                            {isReadOnly && canWindDownPool
                                ? '읽기 전용 회원이라 참여자를 새로 추가할 수 없습니다. 남은 참여자 제거와 공유 종료만 가능합니다.'
                                : '현재는 공유 수강권 조회만 가능합니다. 참여자 변경 기능은 센터 적용 단계에 맞춰 열립니다.'}
                        </p>
                    )}
                </div>

                {events.length > 0 && (
                    <div className="mt-[20px] border-t border-[#EEF1F5] pt-[16px]">
                        <button
                            type="button"
                            className="text-[14px] font-semibold text-[#2B529D] hover:underline"
                            onClick={() => setIsHistoryVisible((visible) => !visible)}
                        >
                            {isHistoryVisible ? '변경 이력 접기' : `변경 이력 보기 (${events.length}건)`}
                        </button>
                        {isHistoryVisible && (
                            <ul
                                aria-label="공유 변경 이력"
                                className="mt-[12px] rounded-[8px] border border-[#E3E8F0] overflow-hidden"
                            >
                                {events.map((event, index) => (
                                    <li
                                        key={`${event.createdAt || ''}-${event.action}-${index}`}
                                        className="px-[16px] py-[12px] border-b last:border-b-0 border-[#EEF1F5]"
                                    >
                                        <div className="flex items-center justify-between gap-[12px]">
                                            <span className="text-[13px] font-semibold text-[#313640]">
                                                {EVENT_ACTION_LABELS[event.action] || event.action}
                                                {event.targetMemberName ? ` · ${event.targetMemberName}` : ''}
                                                {formatEventLimitChange(event)}
                                            </span>
                                            <span className="text-[12px] text-[#7B8493] shrink-0">
                                                {event.createdAt
                                                    ? dayjs(event.createdAt).format('YYYY.MM.DD HH:mm')
                                                    : ''}
                                            </span>
                                        </div>
                                        <p className="mt-[6px] text-[12px] text-[#677080]">
                                            {event.actor?.name ? `${event.actor.name} · ` : ''}
                                            {formatEventReason(event.reason)}
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </section>

            {removeTarget && (
                <ActionModal
                    title="공유에서 회원 제거"
                    description={[
                        `${removeTarget.name} 회원을 이 공유 수강권에서 제거합니다. 앞으로 이 회차로 예약할 수 없게 됩니다.`,
                        // 이미 쓴 회차는 되돌아오지 않는다. 제거 전에 그 숫자를 눈으로 확인하게 한다.
                        shouldShowParticipantUsage(pool)
                            ? `지금까지 사용 ${removeTarget.usedCount}회 · 예약 ${removeTarget.reservedCount}회. 이미 사용한 회차는 복구되지 않습니다.`
                            : '',
                        '미래 일정이나 승인 대기 신청이 남아 있으면 서버가 제거를 차단합니다.',
                    ]
                        .filter(Boolean)
                        .join('\n\n')}
                    confirmText="회원 제거"
                    isDanger
                    isLoading={removeParticipantMutation.isLoading}
                    onClose={closeAction}
                    onConfirm={handleRemoveParticipant}
                >
                    <ReasonField label="제거 사유" value={reason} onChange={setReason} />
                </ActionModal>
            )}

            {coachLinkTarget && (
                <ActionModal
                    title="담당 강사 연결이 필요합니다"
                    description={
                        <div className="flex flex-col gap-[12px]">
                            <p className="m-0 text-[14px] leading-[22px] text-[#677080]">
                                <b className="font-semibold text-[#313640]">{coachLinkTarget.name}</b> 회원은 아직{' '}
                                {coachDisplayName}에게 연결되어 있지 않습니다. 공유 수강권은 담당 강사에
                                연결된 회원만 함께 쓸 수 있습니다.
                            </p>
                            {/* 연결은 이 화면 밖에도 영향이 간다. 무엇이 바뀌는지 밝히고 확인받는다. */}
                            <ul className="m-0 flex list-none flex-col gap-[2px] rounded-[10px] border border-solid border-[#EEF1F5] bg-[#FAFBFD] p-[14px]">
                                <li className="flex items-start gap-[8px] py-[4px] text-[13px] leading-[19px] text-[#505967]">
                                    <span aria-hidden className="mt-[2px] shrink-0 text-[#0A7563]">
                                        <Check size={15} strokeWidth={2.6} />
                                    </span>
                                    <span>
                                        <b className="font-semibold text-[#313640]">{coachDisplayName}에 연결</b> —
                                        이 회원이 {coachDisplayName}의 담당 회원 목록에 나타납니다
                                    </span>
                                </li>
                                <li className="flex items-start gap-[8px] py-[4px] text-[13px] leading-[19px] text-[#505967]">
                                    <span aria-hidden className="mt-[2px] shrink-0 text-[#B4632D]">
                                        <Users size={15} />
                                    </span>
                                    <span>
                                        <b className="font-semibold text-[#313640]">공유에 추가</b> — 연결 직후
                                        이 회차를 함께 쓰게 됩니다
                                    </span>
                                </li>
                            </ul>
                            <p className="m-0 text-[12px] leading-[18px] text-[#98A2B3]">
                                연결은 회원 프로필의 배정된 강사에서 언제든 해제할 수 있습니다. 기존 수강권과
                                결제 기록에는 영향을 주지 않습니다.
                            </p>
                        </div>
                    }
                    confirmText="연결하고 추가"
                    isLoading={addMemberCoachMutation.isLoading || addParticipantMutation.isLoading}
                    onClose={() => setCoachLinkTarget(null)}
                    onConfirm={handleLinkCoachAndAdd}
                >
                    {null}
                </ActionModal>
            )}

            {action === 'close' && (
                <ActionModal
                    title="공유 수강권 종료"
                    description={closeModalDescription}
                    confirmText={needsCleanupAcknowledgement ? '취소하고 종료' : '공유 종료'}
                    isDanger
                    isLoading={closePoolMutation.isLoading}
                    // 프리뷰를 아직 못 받았거나, 취소 대상이 있는데 확인 전이거나,
                    // 한 번에 보낼 수 있는 상한을 넘으면 누를 수 없다.
                    isConfirmDisabled={
                        closePoolMutation.isLoading ||
                        closePreviewQuery.isLoading ||
                        /**
                         * **새 목록을 받아오는 동안 잠근다.**
                         *
                         * SHARE_CLOSE_PLAN_CHANGED 뒤 refetch 를 걸어놓고 버튼을 열어두면,
                         * 그 공백에 다시 눌러 **낡은 목록으로 두 번째 요청**이 나간다.
                         * 확인 서명도 아직 옛 목록 기준이라 그대로 유효하다.
                         * `isFetching` 을 함께 봐야 그 창이 닫힌다.
                         */
                        closePreviewQuery.isFetching ||
                        isCloseCleanupTooLarge ||
                        (needsCleanupAcknowledgement && !isCleanupAcknowledged)
                    }
                    onClose={closeAction}
                    onConfirm={handleClosePool}
                >
                    {closePreviewQuery.isLoading && (
                        <p style={{ fontSize: 13, color: '#677080' }}>
                            종료하면 무엇이 취소되는지 확인하는 중이에요...
                        </p>
                    )}
                    {!closePreviewQuery.isLoading && needsCleanupAcknowledgement && (
                        <div
                            style={{
                                border: '1px solid #F1AEB3',
                                background: '#FFF6F6',
                                borderRadius: 8,
                                padding: '16px 18px',
                                marginBottom: 20,
                            }}
                        >
                            <p style={{ fontSize: 14, fontWeight: 700, color: '#B4232D' }}>
                                예정된 수업 {closeCleanupCount}건이 취소됩니다
                            </p>
                            {/*
                              **누구 것이 몇 건인지 반드시 나눠 보여준다.**
                              남의 수업을 취소하는 일이라, "총 8건"만으로는 관리자가 승인할
                              근거가 없다. 소유자와 참여자를 갈라 이름과 함께 적는다.
                            */}
                            <ul
                                aria-label="취소될 예정된 수업"
                                style={{ marginTop: 12, display: 'grid', gap: 8 }}
                            >
                                {closeCleanupMemberRows.map((row) => (
                                    <li
                                        key={row.memberId || row.label}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                            fontSize: 13,
                                            color: '#3D4244',
                                        }}
                                    >
                                        <span>
                                            <strong style={{ fontWeight: 600 }}>{row.label}</strong>
                                            <span style={{ marginLeft: 6, color: '#8A929E' }}>
                                                {row.roleLabel}
                                            </span>
                                        </span>
                                        <span style={{ whiteSpace: 'nowrap' }}>{row.countLabel}</span>
                                    </li>
                                ))}
                            </ul>
                            <p
                                style={{
                                    marginTop: 14,
                                    fontSize: 12.5,
                                    lineHeight: 1.6,
                                    color: '#8A6100',
                                    background: '#FFF8E8',
                                    borderRadius: 6,
                                    padding: '10px 12px',
                                }}
                            >
                                취소된 수업은 되돌릴 수 없어요. 수업이 취소된 회원에게는 자동으로
                                알림이 갑니다(회원당 1건).
                            </p>
                            <label
                                style={{
                                    marginTop: 14,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    fontSize: 13,
                                    color: '#3D4244',
                                    cursor: 'pointer',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={isCleanupAcknowledged}
                                    onChange={(event) =>
                                        setAcknowledgedItemSignature(
                                            event.target.checked ? closeItemSignature : null,
                                        )
                                    }
                                />
                                위 내용을 확인했고, 수업을 취소한 뒤 공유를 종료합니다.
                            </label>
                        </div>
                    )}
                    {!closePreviewQuery.isLoading && isCloseCleanupTooLarge && (
                        <p
                            style={{
                                marginBottom: 20,
                                fontSize: 13,
                                lineHeight: 1.6,
                                color: '#B4232D',
                            }}
                        >
                            한 번에 정리할 수 있는 건수({SHARE_CLOSE_EXPECTED_ITEM_ID_LIMIT}건)를
                            넘었어요. 예약을 일부 정리한 뒤 다시 시도하거나 운영 담당자에게
                            문의해주세요.
                        </p>
                    )}
                    <ReasonField label="종료 사유" value={reason} onChange={setReason} />
                </ActionModal>
            )}
        </>
    );
};

export default SharedMembershipSection;
